import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GpdButtonComponent } from 'src/app/components/gpd-button/gpd-button.component';
import { DxPopoverModule, DxCalendarModule } from 'devextreme-angular';
import { ValueChangedEvent } from 'devextreme/ui/calendar';
import { CalendarDay, CalendarEntry } from './calendar.types';

// Shared "Better Calendar" (ADO #4484) month panel — nav arrows, the clickable month label with
// its month/year quick-picker, weekday row, and day grid with the "+N" overflow trigger. Extracted
// out of project-calendar so the Contracts-section calendar (#4551) can reuse it verbatim instead
// of duplicating this — by far the most iteratively fine-tuned part of the original component
// (corner radius, popover colors, focus outline). Deliberately ignorant of what an entry "is" —
// consumers pre-resolve each CalendarEntry's chipClass/chipLabel themselves (see calendar.types.ts)
// so this component never needs to know "task vs deadline" or "urgency bucket" vocabulary.
@Component({
    selector: 'app-calendar-month-panel',
    imports: [CommonModule, GpdButtonComponent, DxPopoverModule, DxCalendarModule],
    templateUrl: './calendar-month-panel.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./calendar-month-panel.component.css']
})
export class CalendarMonthPanelComponent {
  /** First-of-month anchor for whichever month is currently displayed. */
  @Input() viewDate: Date;
  /** Full grid of day cells (including leading/trailing days of adjacent months) — building this
   *  (and filtering it by whatever the consumer's own type/urgency filter is) stays the
   *  consumer's job, not this presentational component's. */
  @Input() calendarDays: CalendarDay[] = [];
  @Input() locale = 'de-DE';
  /** Shows this many entries per day cell before collapsing the rest into a "+N" indicator. */
  @Input() maxVisibleDayEntries = 3;
  /** Id of the entry currently shown in the consumer's detail panel, for the active-chip highlight. */
  @Input() selectedEntryId: string | null = null;

  @Output() prevMonth = new EventEmitter<void>();
  @Output() nextMonth = new EventEmitter<void>();
  @Output() today = new EventEmitter<void>();
  /** Emits the first-of-month Date the user picked in the quick-picker. */
  @Output() monthPicked = new EventEmitter<Date>();
  @Output() entryClick = new EventEmitter<CalendarEntry>();
  /** A day cell's "+N" indicator was clicked — the consumer owns whatever panel/popover shows
   *  the full agenda (see CalendarDayAgendaPanelComponent). */
  @Output() dayAgendaOpen = new EventEmitter<CalendarDay>();

  readonly weekdayLabels: string[] = this.buildWeekdayLabels();
  readonly labelToday = $localize`:@@calendarToday:Today`;

  /** Whether the month label's month/year quick-picker popover is open. */
  monthPickerOpen = false;

  goToToday(): void {
    this.today.emit();
  }

  get monthLabel(): string {
    return new Intl.DateTimeFormat(this.locale, { month: 'long', year: 'numeric' }).format(this.viewDate);
  }

  toggleMonthPicker(event: Event): void {
    event.stopPropagation();
    this.monthPickerOpen = !this.monthPickerOpen;
  }

  closeMonthPicker(): void {
    this.monthPickerOpen = false;
  }

  /** dx-calendar locked to zoomLevel "year" (via min/maxZoomLevel in the template) shows a grid of
   *  the 12 months instead of days, so picking one is a single click — no more stepping through
   *  prev/next a month at a time to reach a date far from the current view. */
  onMonthPicked(e: ValueChangedEvent): void {
    if (!e.value) return;
    this.monthPicked.emit(this.firstOfMonth(new Date(e.value)));
    this.closeMonthPicker();
  }

  visibleDayEntries(day: CalendarDay): CalendarEntry[] {
    return day.entries.slice(0, this.maxVisibleDayEntries);
  }

  hiddenDayEntryCount(day: CalendarDay): number {
    return Math.max(0, day.entries.length - this.maxVisibleDayEntries);
  }

  /** Stops propagation so it doesn't also trigger a click on whatever the day cell sits on top of. */
  openDayAgenda(day: CalendarDay, event: Event): void {
    event.stopPropagation();
    this.dayAgendaOpen.emit(day);
  }

  trackByEntryId(_index: number, entry: CalendarEntry): string {
    return entry.id;
  }

  trackByDay(_index: number, day: CalendarDay): number {
    return day.date.getTime();
  }

  private firstOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  private buildWeekdayLabels(): string[] {
    // Monday-start week, matching the Figma grid (Mo…So).
    const monday = new Date(2026, 0, 5); // an arbitrary known Monday
    const formatter = new Intl.DateTimeFormat('de-DE', { weekday: 'short' });
    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(monday);
      day.setDate(monday.getDate() + i);
      return formatter.format(day);
    });
  }
}
